import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  MapPin,
  Building2,
  Home,
  FolderTree,
  Layers,
  Map,
  Briefcase,
  Store,
  BarChart3,
  FileSpreadsheet,
  Upload,
  AlertCircle,
  FileSearch,
  Image,
  Tags,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Globe,
  Menu,
  X,
  Award,
} from 'lucide-react';
import { Button } from '../ui/button';

const menuItems = [
  { key: 'dashboard', icon: LayoutDashboard, path: '/admin' },
  {
    key: 'master_data',
    icon: FolderTree,
    children: [
      { key: 'regions', icon: MapPin, path: '/admin/regions' },
      { key: 'cities', icon: Building2, path: '/admin/cities' },
      { key: 'districts', icon: Home, path: '/admin/districts' },
      { key: 'categories', icon: FolderTree, path: '/admin/categories' },
      { key: 'subcategories', icon: Layers, path: '/admin/subcategories' },
      { key: 'map_categories', icon: Map, path: '/admin/map-categories' },
    ],
  },
  { key: 'projects', icon: Briefcase, path: '/admin/projects' },
  { key: 'success_stories', icon: Award, path: '/admin/success-stories' },
  { key: 'outlets', icon: Store, path: '/admin/outlets' },
  {
    key: 'statistics',
    icon: BarChart3,
    children: [
      { key: 'stat_definitions', icon: FileSpreadsheet, path: '/admin/statistics/definitions' },
      { key: 'stat_values', icon: BarChart3, path: '/admin/statistics/values' },
      { key: 'region_statistics', icon: MapPin, path: '/admin/statistics/region' },
    ],
  },
  {
    key: 'import_center',
    icon: Upload,
    children: [
      { key: 'import_center', icon: Upload, path: '/admin/import' },
      { key: 'import_history', icon: FileSpreadsheet, path: '/admin/import/history' },
      { key: 'import_errors', icon: AlertCircle, path: '/admin/import/errors' },
    ],
  },
  { key: 'review_queue', icon: FileSearch, path: '/admin/review-queue' },
  { key: 'media', icon: Image, path: '/admin/media' },
  { key: 'aliases', icon: Tags, path: '/admin/aliases' },
];

export function Sidebar({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }) {
  const { t, isRTL, toggleLanguage, language } = useLanguage();
  const { logout } = useAuth();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});

  const toggleMenu = (key) => {
    setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderMenuItem = (item, depth = 0) => {
    const Icon = item.icon;
    const isActive = item.path === location.pathname;
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = openMenus[item.key];
    const isChildActive = hasChildren && item.children.some(child => child.path === location.pathname);

    if (hasChildren) {
      return (
        <div key={item.key} className="mb-1">
          <button
            onClick={() => toggleMenu(item.key)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
              "hover:bg-[#0A4C3A]/10 text-gray-700",
              isChildActive && "bg-[#0A4C3A]/10 text-[#0A4C3A]",
              isCollapsed && "justify-center"
            )}
            data-testid={`nav-${item.key}`}
          >
            <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
            {!isCollapsed && (
              <>
                <span className="flex-1 text-start text-sm font-medium">{t(`nav.${item.key}`)}</span>
                {isOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                )}
              </>
            )}
          </button>
          {!isCollapsed && isOpen && (
            <div className={cn("mt-1 space-y-1", isRTL ? "pr-4" : "pl-4")}>
              {item.children.map((child) => renderMenuItem(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.key}
        to={item.path}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors mb-1",
          "hover:bg-[#0A4C3A]/10",
          isActive ? "bg-[#0A4C3A] text-white hover:bg-[#073B2D]" : "text-gray-700",
          isCollapsed && "justify-center"
        )}
        data-testid={`nav-${item.key}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
        {!isCollapsed && <span className="text-sm font-medium">{t(`nav.${item.key}`)}</span>}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 h-full bg-white border-e border-gray-200 z-50 transition-all duration-300",
          isCollapsed ? "w-20" : "w-64",
          isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          isRTL && isMobileOpen && "translate-x-0",
          isRTL && !isMobileOpen && "translate-x-full lg:translate-x-0",
          isRTL ? "right-0 border-l border-r-0" : "left-0"
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {!isCollapsed && (
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 bg-[#0A4C3A] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">ن</span>
              </div>
              <span className="font-bold text-xl text-[#0A4C3A]">{t('app_name')}</span>
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:flex"
            data-testid="toggle-sidebar"
          >
            {isCollapsed ? (
              isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />
            ) : (
              isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileOpen(false)}
            className="lg:hidden"
            data-testid="close-mobile-sidebar"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Navigation */}
        <nav className="p-3 h-[calc(100vh-8rem)] overflow-y-auto">
          {menuItems.map((item) => renderMenuItem(item))}
        </nav>
        
        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200 bg-white">
          <div className={cn("flex gap-2", isCollapsed ? "flex-col items-center" : "")}>
            <Button
              variant="ghost"
              size={isCollapsed ? "icon" : "sm"}
              onClick={toggleLanguage}
              className="flex-shrink-0"
              data-testid="toggle-language"
            >
              <Globe className="w-4 h-4" />
              {!isCollapsed && <span className="ms-2">{language === 'ar' ? 'EN' : 'ع'}</span>}
            </Button>
            <Button
              variant="ghost"
              size={isCollapsed ? "icon" : "sm"}
              onClick={logout}
              className="flex-shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              {!isCollapsed && <span className="ms-2">{t('logout')}</span>}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}

export function Header({ setIsMobileOpen }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  
  // Get page title from path
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return t('nav.dashboard');
    const segment = path.split('/')[1];
    return t(`nav.${segment}`) || segment;
  };
  
  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileOpen(true)}
          className="lg:hidden"
          data-testid="open-mobile-sidebar"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <h1 className="text-lg font-semibold text-gray-900">{getPageTitle()}</h1>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-600">{user?.name || user?.email}</span>
        <div className="w-8 h-8 bg-[#0A4C3A] rounded-full flex items-center justify-center">
          <span className="text-white text-sm font-medium">
            {(user?.name || user?.email || 'A')[0].toUpperCase()}
          </span>
        </div>
      </div>
    </header>
  );
}
